import { API_BASE_URL } from '../config';

class SmartAttendanceService {
  getAuthHeaders() {
    const token = localStorage.getItem('jwtToken') || localStorage.getItem('token') || localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  }

  async getClassLiveReview(className = '', section = '', date = '', session = 'MORNING', academicYear = '') {
    const params = new URLSearchParams();
    if (className) params.append('className', className);
    if (section) params.append('section', section);
    if (date) params.append('date', date);
    if (session) params.append('session', session);
    if (academicYear) params.append('academicYear', academicYear);

    const response = await fetch(`${API_BASE_URL}/api/attendance/class-review?${params.toString()}`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });
    return response.json();
  }

  async finalizeClassAttendance(payload) {
    const response = await fetch(`${API_BASE_URL}/api/attendance/finalize`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload)
    });
    return response.json();
  }

  async manualOverrideAttendance(payload) {
    const response = await fetch(`${API_BASE_URL}/api/attendance/override`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload)
    });
    return response.json();
  }

  async markManualAttendance(data) {
    const response = await fetch(`${API_BASE_URL}/api/attendance/manual`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return response.json();
  }

  async processScan(payload, scanType = 'QR', deviceId = 'DESKTOP-WEB', teacherId = null, scannerLocation = 'Main Gate A', academicYear = '') {
    const response = await fetch(`${API_BASE_URL}/api/attendance/scan`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ payload, scanType, deviceId, teacherId, scannerLocation, academicYear })
    });
    return response.json();
  }

  async getTodaySummary() {
    const response = await fetch(`${API_BASE_URL}/api/attendance/today`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });
    return response.json();
  }

  async getReport(startDate = '', endDate = '', className = '', section = '', academicYear = '') {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (className && className !== 'All') params.append('className', className);
    if (section && section !== 'All') params.append('section', section);
    if (academicYear) params.append('academicYear', academicYear);

    const response = await fetch(`${API_BASE_URL}/api/attendance/report?${params.toString()}`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch student attendance report: ${response.status} ${response.statusText}`);
    }
    return response.json();
  }

  async getTodayTeacherTracker(date = '', academicYear = '') {
    const params = new URLSearchParams();
    if (date) params.append('date', date);
    if (academicYear) params.append('academicYear', academicYear);

    const response = await fetch(`${API_BASE_URL}/api/attendance/tracker?${params.toString()}`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch teacher attendance tracker: ${response.status} ${response.statusText}`);
    }
    return response.json();
  }

  async getDayWiseAttendance(date = '', className = '', section = '', academicYear = '') {
    const params = new URLSearchParams();
    if (date) params.append('date', date);
    if (className && className !== 'All') params.append('className', className);
    if (section && section !== 'All') params.append('section', section);
    if (academicYear) params.append('academicYear', academicYear);

    const response = await fetch(`${API_BASE_URL}/api/attendance/day-wise?${params.toString()}`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch day-wise attendance: ${response.status} ${response.statusText}`);
    }
    return response.json();
  }

  async getClassWiseAttendance(startDate = '', endDate = '', academicYear = '') {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (academicYear) params.append('academicYear', academicYear);

    const response = await fetch(`${API_BASE_URL}/api/attendance/class-wise?${params.toString()}`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch class-wise attendance: ${response.status} ${response.statusText}`);
    }
    return response.json();
  }

  async getDevices() {
    const response = await fetch(`${API_BASE_URL}/api/attendance/devices`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });
    return response.json();
  }

  async registerDevice(deviceData) {
    const response = await fetch(`${API_BASE_URL}/api/attendance/devices`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(deviceData)
    });
    return response.json();
  }

  async getSettings() {
    const response = await fetch(`${API_BASE_URL}/api/attendance/settings`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });
    return response.json();
  }

  async saveSettings(settingsData) {
    const response = await fetch(`${API_BASE_URL}/api/attendance/settings`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(settingsData)
    });
    return response.json();
  }

  async testWhatsAppConnection(testPhone, message = '') {
    const response = await fetch(`${API_BASE_URL}/api/attendance/test-whatsapp`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ testPhone, message })
    });
    return response.json();
  }
}

export default new SmartAttendanceService();
